"use client";

import { motion } from "framer-motion";
import { MAP_THEME, MapStyleKey } from "./mapTheme";

interface PanoramaMarkerProps {
  thumbnailUrl?: string;
  /** Current map zoom level */
  zoom: number;
  /** Number of panoramas grouped at this location */
  count?: number;
  name?: string;
  mapStyle: MapStyleKey;
  onClick: (e: React.MouseEvent) => void;
  /** Feature id — used to seed a deterministic scatter direction per marker */
  featureId?: string;
}

/** Deterministic scatter offset from feature id — keeps animation consistent across re-renders */
function scatterOffset(id: string | undefined): { x: number; y: number } {
  if (!id) return { x: 0, y: 0 };
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (Math.imul(31, h) + id.charCodeAt(i)) | 0;
  const angle = ((h >>> 0) % 360) * (Math.PI / 180);
  const dist = 28 + ((h >>> 8) % 20); // 28–48 px
  return { x: Math.cos(angle) * dist, y: Math.sin(angle) * dist };
}

/**
 * Dual-mode map marker:
 *
 *  LOW  ZOOM (< 7):    Indigo teardrop-pin icon with a lens/count inside.
 *                      Visible from zoom 2 all the way to the crossfade.
 *
 *  HIGH ZOOM (≥ 7.5):  16:9 thumbnail card with a pin triangle.
 *                      Grows from 36 px wide at z10 to 60 px at z16+.
 *
 *  CROSSFADE (7 → 7.5): Both elements coexist at the same bottom anchor so
 *                        the user never loses the photo's location.
 */

// ── Crossfade thresholds ──────────────────────────────────────────────────────
const PHOTO_IN_START = 7;      // photo begins fading in
const PHOTO_IN_END   = 7.5;   // photo fully visible
const ICON_OUT_START = 7;      // icon begins fading out
const ICON_OUT_END   = 8;      // icon fully gone

export default function PanoramaMarker({ thumbnailUrl, zoom, count = 1, name, mapStyle, onClick, featureId }: PanoramaMarkerProps) {
  const t_ = MAP_THEME[mapStyle];
  const isCluster = count > 1;
  const offset = scatterOffset(featureId);

  // Opacity of each mode — they overlap during the crossfade window
  const photoOp = Math.max(0, Math.min(1, (zoom - PHOTO_IN_START) / (PHOTO_IN_END - PHOTO_IN_START)));
  const iconOp  = Math.max(0, Math.min(1, 1 - (zoom - ICON_OUT_START) / (ICON_OUT_END - ICON_OUT_START)));

  // Photo card size: 36 px wide at z10, 60 px at z16+
  const t     = Math.max(0, Math.min(1, (zoom - 10) / 6));
  const cardW = Math.round(36 + t * 24);   // 36 → 60 px
  const cardH = Math.round(cardW * 0.5625); // 16:9

  const ariaLabel = isCluster
    ? `${count} panoramas — click to zoom in`
    : name
      ? `Open panorama: ${name}`
      : "Open 360° panorama";

  return (
    <motion.div
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(e as unknown as React.MouseEvent);
        }
      }}
      initial={{ opacity: 0, scale: 0, x: offset.x, y: offset.y }}
      animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
      exit={{ opacity: 0, scale: 0, x: offset.x * 0.5, y: offset.y * 0.5 }}
      whileHover={{ scale: 1.1 }}
      transition={{ type: "spring", stiffness: 130, damping: 22, mass: 1.6 }}
      style={{
        cursor: isCluster ? "zoom-in" : "pointer",
        userSelect: "none",
        position: "relative",
        // Minimum 44×44 px touch target (WCAG 2.5.5 / Apple HIG).
        // Extra height is added above the anchor point — visually transparent.
        width: Math.max(44, cardW),
        height: Math.max(44, 28),
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-end",
        filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.60))",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.filter =
          `drop-shadow(0 4px 14px ${t_.glow}) brightness(1.12)`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.filter =
          "drop-shadow(0 2px 6px rgba(0,0,0,0.60))";
      }}
    >
      {/* ── Icon pin (low zoom) ─────────────────────────────────────────────
          In normal flow so the container height (28 px) matches the pin.
          The SVG tip sits at the very bottom = geographic coordinate.       */}
      <svg
        viewBox="0 0 20 28"
        width="20"
        height="28"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          position: "absolute",
          bottom: 0,
          opacity: iconOp,
          transition: "opacity 0.5s ease",
          pointerEvents: iconOp < 0.05 ? "none" : "auto",
          flexShrink: 0,
        }}
      >
        {/* Teardrop body */}
        <path
          d="M10 1 C4.925 1 1 4.925 1 10 C1 15.7 10 27 10 27 C10 27 19 15.7 19 10 C19 4.925 15.075 1 10 1 Z"
          fill={isCluster ? "#f59e0b" : t_.pin}
          stroke={t_.pinStroke}
          strokeWidth="1.5"
        />
        {count > 1 ? (
          /* Count number for grouped markers */
          <text
            x="10"
            y="13.5"
            textAnchor="middle"
            fontSize={count > 9 ? "6" : "7"}
            fontWeight="800"
            fill="white"
            fontFamily="system-ui, -apple-system, sans-serif"
          >
            {count > 99 ? "99+" : String(count)}
          </text>
        ) : (
          /* Camera-lens rings for single markers */
          <>
            <circle cx="10" cy="10" r="4.5" fill="none" stroke={t_.pinStroke} strokeWidth="1.2" />
            <circle cx="10" cy="10" r="1.8" fill={t_.pinStroke} />
          </>
        )}
      </svg>

      {/* ── Photo card (high zoom) ──────────────────────────────────────────
          Absolutely positioned with bottom:0 so its own pin triangle tip
          lands at the exact same geographic coordinate as the icon tip.    */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          opacity: photoOp,
          transition: "opacity 0.5s ease",
          pointerEvents: photoOp < 0.05 ? "none" : "auto",
        }}
      >
        {/* Thumbnail */}
        <div
          style={{
            width: cardW,
            height: cardH,
            borderRadius: 4,
            overflow: "hidden",
            border: `1.5px solid ${t_.cardBorder}`,
            boxShadow: isCluster
              ? `0 0 0 1px ${t_.ring}, 3px -3px 0 0 ${t_.cardBg}, 3px -3px 0 1.5px ${t_.cardBorder}, 6px -6px 0 0 ${t_.cardBg}, 6px -6px 0 1.5px ${t_.cardBorder}`
              : `0 0 0 1px ${t_.ring}`,
            background: t_.cardBg,
            position: "relative",
            transition: "width 0.25s ease, height 0.25s ease",
          }}
        >
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              draggable={false}
              style={{
                objectFit: "cover",
                display: "block",
                width: "100%",
                height: "100%",
                pointerEvents: "none",
              }}
            />
          ) : (
            <div style={{
              width: "100%",
              height: "100%",
              background: t_.fallback,
            }} />
          )}

          {/* Count badge — top-right corner */}
          {count > 1 && (
            <div style={{
              position: "absolute",
              top: 2,
              right: 2,
              background: t_.badge,
              backdropFilter: "blur(4px)",
              border: `1px solid ${t_.badgeBorder}`,
              borderRadius: 999,
              padding: "1px 4px",
              fontSize: 9,
              fontWeight: 800,
              color: t_.badgeText,
              lineHeight: "12px",
              letterSpacing: "0.03em",
            }}>
              {count > 99 ? "99+" : count}
            </div>
          )}
        </div>

        {/* Pin triangle — tip at the geographic coordinate */}
        <div style={{
          width: 0,
          height: 0,
          borderLeft: "4px solid transparent",
          borderRight: "4px solid transparent",
          borderTop: `5px solid ${t_.cardBorder}`,
          marginTop: -1,
        }} />
      </div>
    </motion.div>
  );
}
